import React from 'react'
import styled from '@emotion/styled'
import * as mixins from 'view/styles/mixins'
import * as constants from 'view/styles/constants'
import { MultiButton, MultiButtonProps, ButtonStrip } from '../buttons'
import { iconSet } from '../icons/iconSet'
import { DropdownOptions } from '../menus'
import { Tooltip } from '../tooltip'
import { isNil } from 'lodash-es'

export enum SplitButtonType {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Good = 'Good',
  Bad = 'Bad',
  BadOutline = 'BadOutline',
  BadLight = 'BadLight',
  BadSquare = 'BadSquare',
  Warn = 'Warn'
}

const getSplitButtonColor = (type: SplitButtonType, accented: boolean, isBorder?: boolean) => {
  switch (type) {
    case SplitButtonType.Primary:
      return accented ? constants.ACCENT_PRIMARY_1 : constants.ACCENT_PRIMARY

    case SplitButtonType.Secondary:
      return accented ? constants.ACCENT_SECONDARY_1 : constants.ACCENT_SECONDARY_2

    case SplitButtonType.Good:
      return accented ? constants.ACCENT_GOOD_1 : constants.ACCENT_GOOD

    case SplitButtonType.Bad:
    case SplitButtonType.BadSquare:
      return accented ? constants.ACCENT_BAD_1 : constants.ACCENT_BAD

    case SplitButtonType.BadLight:
      return accented ? constants.ACCENT_BAD_1 : constants.ACCENT_BAD_2

    case SplitButtonType.Warn:
      return accented ? constants.ACCENT_BE_AWARE_1 : constants.ACCENT_BE_AWARE

    case SplitButtonType.BadOutline:
      return accented || isBorder ? constants.ACCENT_BAD_1 : constants.CORE_PRIMARY

    default:
      throw new Error(`Unexpected SplitButtonType [${type}]`)
  }
}

const getTextColor = (type: SplitButtonType, accented: boolean) => {
  if (type === SplitButtonType.BadOutline) {
    return accented ? constants.CORE_PRIMARY : constants.ACCENT_BAD_1
  }

  return constants.CORE_PRIMARY
}

interface SplitButtonProps extends MultiButtonProps {
  hasWarning?: boolean
  type?: SplitButtonType
}

const buttonHeight = '26px'

export const SplitButton = styled(MultiButton)<SplitButtonProps>`
  ${mixins.noWrap}
  overflow: hidden;
  align-items: center;
  justify-content: center;
  font-weight: ${constants.FontWeight.Bold};
  font-size: 12px;
  width: ${props => (props.type === SplitButtonType.BadSquare ? buttonHeight : 'auto')};
  height: ${buttonHeight};
  max-width: ${props => (props.type === SplitButtonType.BadSquare ? buttonHeight : 'none')};
  min-width: ${props => (props.type === SplitButtonType.BadSquare ? buttonHeight : 'auto')};
  flex: 1 1 0;
  outline: none;
  padding: ${props => (props.type === SplitButtonType.BadSquare ? 0 : '0 10px')};
  border-width: 2px;
  border-style: solid;
  color: ${props => (props.disabled ? constants.CORE_PRIMARY : props.type ? getTextColor(props.type, false) : constants.CORE_PRIMARY_7)};
  background-color: ${props =>
    props.disabled ? constants.CORE_PRIMARY_3 : props.type ? getSplitButtonColor(props.type, false) : constants.CORE_PRIMARY};
  border-color: ${props =>
    props.disabled ? constants.CORE_PRIMARY_3 : props.type ? getSplitButtonColor(props.type, false, true) : constants.CORE_PRIMARY_3};
  border-radius: 6px;

  &:not(:hover) {
    opacity: ${props => (props.hasWarning === true ? '0.5' : 1)};
  }

  &:hover:not(:disabled) {
    color: ${props => (props.type ? getTextColor(props.type, true) : undefined)};
    background-color: ${props => (props.type ? getSplitButtonColor(props.type, true) : constants.CORE_PRIMARY)};
    border-color: ${props => (props.type ? getSplitButtonColor(props.type, true) : constants.CORE_PRIMARY_5)};
  }

  &:not(:first-of-type) {
    margin-left: 2px;
  }
`

interface ToggleButtonProps extends SplitButtonProps {
  menuOpen: boolean
}

const ToggleButton = styled(SplitButton)<ToggleButtonProps>`
  display: flex;
  background-color: ${props => (props.menuOpen ? constants.ACCENT_PRIMARY_1 : props.disabled ? constants.CORE_PRIMARY_3 : 'transparent')};
  color: ${props => (props.menuOpen ? constants.CORE_PRIMARY : props.disabled ? constants.CORE_PRIMARY : constants.CORE_PRIMARY_5)};
  border-color: transparent;
  margin: 0;
  padding: 0;

  &:hover:not(:disabled) {
    background-color: ${props => (props.menuOpen ? constants.ACCENT_PRIMARY_1 : constants.CORE_PRIMARY)};
    border-color: ${props => (props.menuOpen ? constants.ACCENT_PRIMARY_1 : constants.CORE_PRIMARY_3)};
  }

  width: 26px;
  min-width: 26px;
  flex-grow: 0;
  svg {
    padding-right: 0;
    width: auto;
    height: 100%;
  }
`

export interface ActionItem {
  id?: string
  buttonLabel?: string
  label: string
  type?: SplitButtonType
  onClick: () => unknown
  enabled?: boolean
  warnings?: string[]
  hidden?: boolean
}

export interface SplitButtonDropdownProps {
  className?: string
  withPortal?: boolean
  disabled?: boolean
  autoHideEllipsis?: boolean
  ellipsisAriaLabel?: string
  actions: ActionItem[]
  maxButtons: number
  showDisabledActions?: boolean
  noActionsTooltip?: string
  dataTestId?: string
}

export const SplitButtonDropdown = ({
  actions,
  className,
  maxButtons,
  disabled,
  autoHideEllipsis,
  ellipsisAriaLabel,
  showDisabledActions,
  noActionsTooltip = '',
  dataTestId,
  withPortal
}: SplitButtonDropdownProps) => {
  const actionsFiltered = actions.filter(a => !a.hidden)

  const enabledActions = actionsFiltered.filter(action => action.enabled !== false)
  const availableActions = enabledActions.length > 0 || !showDisabledActions ? enabledActions : actionsFiltered
  const visibleButtonActions = availableActions.slice(0, maxButtons)
  const showEllipsis = autoHideEllipsis ? availableActions.length > visibleButtonActions.length : availableActions.length > 0

  return (
    <DropdownOptions
      actions={actionsFiltered}
      className={className}
      data-test-id={dataTestId}
      disabled={disabled}
      showDisabledActions={showDisabledActions}
      withPortal={withPortal}>
      {({ toggle, isOpen }) => (
        <Tooltip tooltip={showDisabledActions && enabledActions.length === 0 ? noActionsTooltip : ''}>
          <ButtonStrip>
            {visibleButtonActions.map((action, idx) => (
              <Tooltip tooltip={action.warnings?.length ? action.warnings.join('') : ''} key={idx}>
                <SplitButton
                  dataTestId={`SplitButtonDropdown-${dataTestId}-${action.label}-Button`}
                  onClick={evt => handleButtonClick(action, evt)}
                  type={action.type}
                  hasWarning={!isNil(action.warnings) && action.warnings.length > 0}
                  disabled={disabled || action.enabled === false}>
                  {action.buttonLabel || action.label}
                </SplitButton>
              </Tooltip>
            ))}
            {showEllipsis && (
              <ToggleButton
                aria-label={ellipsisAriaLabel ?? 'More Options'}
                dataTestId={`SplitButtonDropdown-${dataTestId}-Icon`}
                menuOpen={isOpen}
                onClick={toggle}
                disabled={disabled}>
                {iconSet.menuIcon}
              </ToggleButton>
            )}
          </ButtonStrip>
        </Tooltip>
      )}
    </DropdownOptions>
  )
}

function handleButtonClick(action: ActionItem, evt: React.MouseEvent) {
  action.onClick()
  evt.preventDefault()
  evt.stopPropagation()
}
